import { inject, Injectable, OnDestroy } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { AllDeviceInfo, UserProcess, UserProcessMode } from '@interfaces';

import { LocalStorageService } from '@core/services/local-storage.service';
import { DeviceInfoService } from '@core/services/device-info.service';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ModeChangedModalComponent } from './mode-changed-modal.component';

export interface ModalOpts {
  userProcess: UserProcess;
}

@Injectable({
  providedIn: 'root',
})
export class ModeChangedModalService implements OnDestroy {
  private readonly dialog = inject(MatDialog);
  private readonly lsService = inject(LocalStorageService);
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly mobileDialogWidth = 'calc(100vw - 32px)';
  private readonly mobileDialogMaxHeight = 'calc(100dvh - 32px)';

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  public name = 'mode-changed-modal';
  public deviceInfo: AllDeviceInfo;
  private dialogWidth = '850px';
  constructor() {
    this.deviceInfo = this.deviceInfoService.getInfo();

    this.deviceInfoService.infoEmitter.pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.deviceInfo = info;
      if (this.deviceInfo.deviceTypeDetected === 'PHONE') {
        this.dialogWidth = this.mobileDialogWidth;
      }
    });

    if (this.deviceInfo.deviceTypeDetected === 'PHONE') {
      this.dialogWidth = this.mobileDialogWidth;
    }
  }

  public open(opts: ModalOpts): MatDialogRef<ModeChangedModalComponent> {
    const isPhone = this.deviceInfo.deviceTypeDetected === 'PHONE';

    this.lsService.setPostModeExplanationModalSeen(opts.userProcess.id, true);

    return this.dialog.open(ModeChangedModalComponent, {
      width: this.dialogWidth,
      maxWidth: this.dialogWidth,
      ...(isPhone ? { maxHeight: this.mobileDialogMaxHeight } : {}),
      panelClass: ['mode-changed-modal'],
      data: { ...opts },
      autoFocus: false,
      backdropClass: ['user-process-modal-backdrop', this.name + '_backdrop'],
    });
  }

  public openIfPostModeAndNotSeen(opts: ModalOpts): MatDialogRef<ModeChangedModalComponent> | null {
    const { id: userProcessId, mode: userProcessMode } = opts.userProcess;
    const explanationSeen = this.lsService.getPostModeExplanationModalSeen(userProcessId);
    const isPostMode = userProcessMode === UserProcessMode.POST;
    if (isPostMode && !explanationSeen) {
      return this.open(opts);
    }
    return null;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
